import {
  APP_ACP,
  APP_CAREPROVIDER,
  APP_CLINIC,
  APP_PROVIDER_SEARCH,
} from "core/consts";
import {
  ENV_DEMO,
  ENV_DEVELOPMENT,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
  isReceiverApp,
  isSenderApp,
} from "core/model/config";
import { AppType, Env } from "core/types";

/**
 * Infers the environment from window.location
 */
function inferEnvFromHostname(hostname: string): Env {
  if (hostname.includes("localhost")) return ENV_DEVELOPMENT;
  if (hostname.includes("-demo.recaresolutions.com")) return ENV_DEMO;
  if (hostname.includes("-preprod.recaresolutions.com")) return ENV_PREPROD;

  if (hostname.endsWith(".staging.recaresolutions.com")) {
    return ENV_STAGING;
  }

  if (hostname.endsWith(".recaresolutions.com")) return ENV_PRODUCTION;

  return ENV_PRODUCTION;
}

/**
 * Gets the app path for the given app and env context
 */
function getAppPath(app: AppType, forProd: boolean): string {
  if (isSenderApp(app)) return "app";
  if (isReceiverApp(app)) return forProd ? "partner" : "careprovider";
  if (app === APP_PROVIDER_SEARCH) return "providersearch";
  return "acp";
}

/**
 * Returns the leading path segment for any given AppType,
 * e.g. APP_CLINIC ⇒ "/app", APP_ACP ⇒ "/acp", APP_PROVIDER_SEARCH ⇒ "/providersearch", etc.
 */
export function getAppRoutePrefix(
  app: AppType,
): "/app" | "/acp" | "/careprovider" | "/providersearch" {
  switch (app) {
    case APP_CLINIC:
      return "/app";
    case APP_ACP:
      return "/acp";
    case APP_CAREPROVIDER:
      return "/careprovider";
    case APP_PROVIDER_SEARCH:
      return "/providersearch";
    default:
      throw new Error(`Unknown app type: ${app}`);
  }
}

/**
 * Replaces the first part of a PR subdomain with the given app type (e.g. app → partner)
 */
function replaceAppPrefixInSubdomain(
  subdomain: string,
  newAppPrefix: string,
): string {
  const parts = subdomain.split("-");
  if (parts.length === 0) return newAppPrefix;

  parts[0] = newAppPrefix;
  return parts.join("-");
}

/**
 * Returns the base URL for a given app and (optional) env
 */
export function getBaseUrl({ app, env }: { app: AppType; env?: Env }): string {
  const { host, hostname, protocol, search } = window.location;

  const resolvedEnv = env ?? inferEnvFromHostname(hostname);
  const baseDomain = "recaresolutions.com";

  const isLocalhost = hostname.includes("localhost");
  const isPrStaging = hostname.endsWith(".staging.recaresolutions.com");

  const forceLocal = env === ENV_DEVELOPMENT;
  const keepCurrentDomain = isLocalhost || isPrStaging || forceLocal;

  const appPathProd = getAppPath(app, true);

  const queryParam = env ?? new URLSearchParams(search).get("env");
  const queryString = queryParam ? `?env=${queryParam}` : "";

  // Keep current hostname in dev or PR link, only change path and query param
  if (keepCurrentDomain) {
    if (isLocalhost) {
      return `${protocol}//${host}${queryString}`;
    }

    const subdomain = hostname.split(`.staging.${baseDomain}`)[0];
    const replaced = replaceAppPrefixInSubdomain(subdomain, appPathProd);
    return `${protocol}//${replaced}.staging.${baseDomain}${queryString}`;
  }

  // Fixed domains for demo/preprod
  if (resolvedEnv === ENV_DEMO || resolvedEnv === ENV_PREPROD) {
    return `${protocol}//${appPathProd}-${resolvedEnv}.${baseDomain}`;
  }

  // Default (production)
  return `${protocol}//${appPathProd}.${baseDomain}`;
}

/**
 * Build a full URL for a given app/env + optional path,
 * carrying over the current window.location.search.
 */
export function buildAppUrl(opts: {
  app: AppType;
  env?: Env;
  path?: string;
  preserveSearch?: boolean;
}): string {
  const { app, env, path = "", preserveSearch = true } = opts;
  const base = getBaseUrl({ app, env });

  const url = new URL(path, base);

  if (preserveSearch) {
    const incomingParams = new URLSearchParams(window.location.search);

    // Params from `path` take precedence — only carry over incoming params
    // that aren't already set by the path.
    const pathParams = new URLSearchParams(url.search);
    for (const key of pathParams.keys()) {
      incomingParams.delete(key);
    }

    const merged = new URLSearchParams([
      ...pathParams.entries(),
      ...incomingParams.entries(),
    ]);
    const result = merged.toString();
    url.search = result ? `?${result}` : "";
  }

  return url.toString();
}
